import { useState, useEffect, useRef, useMemo } from "react";
import styles from "./Style.module.css";
import SearchBar from "../../Search/SearchBar";
import CrossIcon from "../../../Assets/images/cross.svg";

const MEMBER_RATE_LIST_RAW = [
	{
		id: 1,
		title: "Member Rate",
	},
	{
		id: 2,
		title: "Member Rate (OP)",
	},
];

const MemberRateDropdown = ({
	bookingItemId,
	onSelect,
	selectedOption: externalSelectedOption,
	onDropdownToggle = null,
	disabled = false,
	isPremiumOutlet = false,
}) => {

	const memberRateList = useMemo(() => {
		if (isPremiumOutlet) {
			return MEMBER_RATE_LIST_RAW.filter((item) => item.id !== 2);
		}
		return MEMBER_RATE_LIST_RAW;
	}, [isPremiumOutlet]);
	
	const [selectedOption, setSelectedOption] = useState({});
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);
	const searchInputRef = useRef(null);
	// Sync internal state when parent clears selection (e.g. "Apply membership discount" unchecked)
	useEffect(() => {
		if (!externalSelectedOption || !externalSelectedOption?.item) {
			setSelectedOption({});
		} else {
			setSelectedOption(externalSelectedOption);
		}
	}, [externalSelectedOption]);

	// Use external selectedOption if provided, otherwise use internal state
	const currentSelectedOption = externalSelectedOption?.item ? externalSelectedOption : selectedOption?.item ? selectedOption : {};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);



	// Filter and map discount items for display
	const filteredDiscounts = useMemo(() => {
		const list = Array.isArray(memberRateList) ? memberRateList : [];
		const q = (searchQuery || "").trim().toLowerCase();
		return list
			.filter((item) => {
				const name = (item.name || item.label || item.title || "").toString().toLowerCase();
				return !q || name.includes(q);
			})
			.map((item) => ({
				...item,
				displayName: item?.title,
			}));
	}, [memberRateList, searchQuery]);



	// Focus search input when dropdown opens
	useEffect(() => {
		if (isDropdownOpen && searchInputRef.current) {
			// Small delay to ensure the dropdown is rendered
			setTimeout(() => {
				searchInputRef.current?.focus();
				searchInputRef.current?.select();
			}, 0);
		}
	}, [isDropdownOpen]);

	const handleSelect = (item) => {
		const itemWithBookingId = { ...item, bookingItemId };
		const selectedOptionData = { item: itemWithBookingId };
		setSelectedOption(selectedOptionData);
		if (onSelect) {
			onSelect(itemWithBookingId);
		}
		setDropdownOpen(false);
		setSearchQuery("");
	};

	const handleClearDiscount = (e) => {
		e.stopPropagation();
		setSelectedOption({});
		if (onSelect) {
			onSelect(null);
		}
	};

	

	return (
		<div
			ref={dropdownRef}
			className={`${styles.memberRateDropdown} commonDropdownNoImage position-relative ${disabled ? "disabled" : ""}`}
			tabIndex={0}
			>
			<div className={`dropdownToggle ${styles.dropdownToggle}`}
				onClick={() => {
					const newState = !isDropdownOpen;
					setDropdownOpen(newState);
					if (onDropdownToggle) {
						onDropdownToggle(newState);
					}
				}}>
				{currentSelectedOption?.item ? (
					<div className={styles.selectedDiscount}>
						<span className={styles.selectedDiscountText}>
							{currentSelectedOption.item.displayName ?? currentSelectedOption.item.name ?? currentSelectedOption.item.label ?? currentSelectedOption.item.title ?? "Discount"}
						</span>
						<button
							type="button"
							className={styles.selectedDiscountClose}
							onClick={handleClearDiscount}
							aria-label="Remove Member">
							<img src={CrossIcon} alt="" width={10} height={10} />
						</button>
					</div>
				) : (
					<div className="hover-underline">
						+ Add Member
					</div>

				)}
		</div>
		{isDropdownOpen && (
			<div className={`${styles.dropdownContainer}`} ref={dropdownMenuRef}>
				{/* Search Bar */}
				<div className={styles.header}>
					<SearchBar
						ref={searchInputRef}
						placeholder="Search..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						// onKeyDown={handleKeyDown}
						className="border-0"
					/>
				</div>
				<div className={`${styles.dropdownMenu}`}>
					<ul className={styles.bottomList}>
						{filteredDiscounts.map((item) => (
							<li
								key={item.id ?? item.displayName}
								className={`${styles.item} ${currentSelectedOption?.item?.id === item.id ? styles.selectedItem : ""}`}
								onClick={() => handleSelect(item)}>
								<span className={styles.left}>{item.displayName}</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		)}
	</div>
	);
};

export default MemberRateDropdown;
